import type { AiCostDimension } from '@Application/AiCosts/AiCostDimension';
import FetchAiCostBreakdown from '@Application/AiCosts/Queries/FetchAiCostBreakdown';
import fetchAiCostBreakdown from '@Application/AiCosts/fetchAiCostBreakdown';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import aiCostsFiltersSignal from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import type AiCostBreakdownListView from '@UI/Store/AiCosts/Views/AiCostBreakdownListView';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Result = {
	/** The pages loaded so far for the current period and filters; `null` until the first page arrives. */
	readonly list: AiCostBreakdownListView | null;
	readonly error: unknown;
	readonly refetch: () => void;
	readonly showMore: () => void;
	readonly isLoadingMore: boolean;
};

/**
 * Reads the breakdown of one axis for the current period and filters. The component that calls it is
 * mounted under the query key, so the key never changes during its life: "show more" dispatches the
 * next page directly instead of going through the reader.
 */
const useAiCostBreakdown = (accountId: number, dimension: AiCostDimension, rows: number): Result => {
	const dispatch = useDispatch();
	const filters = useSignalValue(aiCostsFiltersSignal);
	const queryKey = filters.tabQueryParams.toString();
	const [isLoadingMore, setIsLoadingMore] = React.useState(false);

	const isCurrent = React.useCallback(
		(value: AiCostBreakdownListView | null): value is AiCostBreakdownListView =>
			value !== null && value.accountId === accountId && value.queryKey === queryKey,
		[accountId, queryKey],
	);

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.aiCostsStore.breakdowns[dimension],
		stopOn: isCurrent,
		query: () =>
			dispatch(
				fetchAiCostBreakdown(
					new FetchAiCostBreakdown(accountId, dimension, filters.tabQueryParams, 0, rows),
				),
			),
		deps: [accountId, queryKey],
	});

	const list = isCurrent(data) ? data : null;

	const showMore = React.useCallback(() => {
		if (list === null) {
			return;
		}

		setIsLoadingMore(true);
		dispatch(
			fetchAiCostBreakdown(
				new FetchAiCostBreakdown(
					accountId,
					dimension,
					filters.tabQueryParams,
					list.loadedPage + 1,
					rows,
				),
			),
		)
			.catch(ignoreHandledError)
			.finally(() => setIsLoadingMore(false));
	}, [accountId, dimension, dispatch, filters.tabQueryParams, list, rows]);

	return { list, error, refetch, showMore, isLoadingMore };
};

export default useAiCostBreakdown;
